import React, { useEffect, useState } from 'react';
import type { ABVariantStat } from '../../types/campaign';

interface ABTestStatsProps {
  campaignId: number;
  fetchStats: (period: string) => Promise<ABVariantStat[]>;
}

const PERIOD_OPTIONS = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
];

const VARIANT_BAR_COLORS = [
  'bg-indigo-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
];

export function ABTestStats({ campaignId, fetchStats }: ABTestStatsProps) {
  const [period, setPeriod] = useState('30d');
  const [stats, setStats] = useState<ABVariantStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchStats(period)
      .then(data => { setStats(data); setLoading(false); })
      .catch(() => { setError('Failed to load A/B stats'); setLoading(false); });
  }, [campaignId, period]);

  const maxTotal = Math.max(...stats.map(s => s.total), 1);

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
            <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-900">A/B Test Performance</h3>
        </div>
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="animate-pulse space-y-2">
              <div className="h-4 bg-gray-100 rounded w-1/4" />
              <div className="h-2 bg-gray-100 rounded w-full" />
              <div className="h-3 bg-gray-100 rounded w-1/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || stats.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
            <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-900">A/B Test Performance</h3>
        </div>
        <p className="text-sm text-gray-400 mt-3">
          {error ?? 'No A/B test data yet. Traffic will appear here once the test starts.'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
            <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-900">A/B Test Performance</h3>
        </div>

        {/* Period selector */}
        <div className="flex gap-1">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`text-xs px-3 py-1 rounded-md font-medium transition-colors ${
                period === opt.value
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats rows */}
      <div className="divide-y divide-gray-50">
        {stats.map((stat, index) => {
          const barColor = VARIANT_BAR_COLORS[index % VARIANT_BAR_COLORS.length];
          const widthPct = Math.round((stat.total / maxTotal) * 100);
          const humanBarPct = stat.total > 0 ? Math.round((stat.humans / stat.total) * 100) : 0;

          return (
            <div key={stat.variant_id} className="px-5 py-4">
              {/* Variant header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${barColor} flex-shrink-0`} />
                  <span className="text-sm font-semibold text-gray-800">
                    {stat.name}
                  </span>
                  {stat.is_deleted && (
                    <span className="text-xs text-red-500 bg-red-50 px-1.5 py-0.5 rounded">
                      Deleted
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span>{stat.total.toLocaleString()} requests</span>
                  <span className={`font-semibold ${stat.human_pct >= 80 ? 'text-emerald-600' : stat.human_pct >= 60 ? 'text-amber-600' : 'text-red-500'}`}>
                    {stat.human_pct}% human
                  </span>
                </div>
              </div>

              {/* URL */}
              <p className="text-xs text-gray-400 mb-2 truncate" title={stat.url}>{stat.url}</p>

              {/* Human/bot bar */}
              <div className="flex h-1.5 rounded-full overflow-hidden bg-gray-100 gap-px">
                <div
                  className="bg-emerald-400 transition-all rounded-l-full"
                  style={{ width: `${humanBarPct}%` }}
                />
                <div
                  className="bg-red-300 transition-all rounded-r-full flex-1"
                />
              </div>

              {/* Sub-stats */}
              <div className="flex gap-4 mt-1.5 text-xs text-gray-400">
                <span>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1" />
                  {stat.humans.toLocaleString()} human
                </span>
                <span>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-300 mr-1" />
                  {stat.bots.toLocaleString()} bot
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
